import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Wordmark } from '@/lib/bricks';
import SiteTimeline from '@/components/developer/SiteTimeline';
import { placeLine, shortDate } from '@/components/developer/siteShareModel';
import { headerLine } from '@/components/developer/siteTimelineModel';
import { creditLine } from '@/components/developer/siteReportModel';
import { theme, Icon, F, T } from '@/components/developer/siteUi';
import BricksLoader from '@/components/BricksLoader';

const t = theme('dark');
const CSS = `
.ss-wrap{max-width:1280px;margin:0 auto;padding:0 40px;box-sizing:border-box}
.ss-share-long{display:inline}.ss-share-short{display:none}
@media (max-width:719px){.ss-wrap{padding:0 20px}.ss-share-long{display:none}.ss-share-short{display:inline}}
`;

// Public, read-only Site Progress view for a builder's stakeholders
// (GC, lender, asset manager). No sign-in; the link itself expires.
export default function SiteSharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const BASE = process.env.REACT_APP_BACKEND_URL;
  const loadMapPreview = useCallback(async (id) => {
    const r = await fetch(`${BASE}/api/dev/share/${encodeURIComponent(token)}/deliverables/${encodeURIComponent(id)}/map-preview`);
    const b = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(typeof b.detail === 'string' ? b.detail : 'Couldn’t draw this map right now. Try again.');
    return b.src || null;
  }, [BASE, token]);

  useEffect(() => {
    let live = true;
    fetch(`${BASE}/api/dev/share/${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) {
          let detail = r.status === 404 ? 'This link is invalid, expired or was turned off.' : 'This page is unavailable right now. Try again shortly.';
          try { const b = await r.json(); if (typeof b?.detail === 'string') detail = b.detail; } catch { /* keep */ }
          throw new Error(detail);
        }
        return r.json();
      })
      .then((d) => { if (live) setData(d); })
      .catch((e) => { if (live) setError(e.message || 'This page is unavailable right now.'); });
    return () => { live = false; };
  }, [BASE, token]);

  useEffect(() => {
    const prev = document.body.style.background;
    document.body.style.background = t.bg;
    return () => { document.body.style.background = prev; };
  }, []);

  const until = data ? shortDate(data.expires_at) : '';
  const line = data ? headerLine(data.timeline && data.timeline.summary) : '';

  return (
    <div style={{ minHeight: '100vh', background: t.bg, color: t.ink, fontFamily: F.display, overflowX: 'clip' }}>
      <style>{CSS}</style>
      <header style={{ borderBottom: `1px solid ${t.hair}` }}>
        <div className="ss-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, height: 72 }}>
          <a href="https://www.brxs.ca" aria-label="Bricks" style={{ display: 'inline-flex', minHeight: 44, alignItems: 'center', textDecoration: 'none' }}>
            <Wordmark size={20} color={T.moon} />
          </a>
          {data && until ? (
            <span data-testid="share-status" style={{ fontSize: 13, color: t.mute, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <span className="ss-share-long">{data.label ? `Shared with ${data.label} · ` : ''}Link valid until {until}</span>
              <span className="ss-share-short">Valid until {until}</span>
            </span>
          ) : <span style={{ fontSize: 13, color: t.mute }}>Site progress</span>}
        </div>
      </header>

      <div className="ss-wrap">
        {error ? (
          <div data-testid="share-error" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ maxWidth: 420, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
              <Icon name="link" size={32} color={t.mute} />
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 500 }}>This link isn’t available</h1>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: t.mute }}>{error} Ask the person who shared it for a new link.</p>
            </div>
          </div>
        ) : !data ? (
          <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BricksLoader variant="assemble" size={40} color={T.brick} label="Loading site progress" />
          </div>
        ) : (
          <main>
            <div style={{ padding: 'clamp(32px, 6vw, 72px) 0 clamp(32px, 4vw, 48px)' }}>
              <p style={{ margin: '0 0 12px', fontSize: 14, color: t.mute }}>Site progress</p>
              <h1 style={{ margin: 0, fontWeight: 500, fontSize: 'clamp(32px, 5.2vw, 60px)', lineHeight: 1.04, letterSpacing: '-0.025em', overflowWrap: 'anywhere' }}>{data.project?.name}</h1>
              {placeLine(data.project) ? <p style={{ margin: '12px 0 0', fontSize: 17, color: t.ink2 }}>{placeLine(data.project)}</p> : null}
              {line ? <p data-testid="share-summary" style={{ margin: '20px 0 0', fontSize: 14, color: t.mute }}>{line}</p> : null}
            </div>
            <SiteTimeline tone="dark" showSummary={false} timeline={data.timeline} phases={[]} project={data.project} sampleElevation={async (id, x, y) => {
              const r = await fetch(`${BASE}/api/dev/share/${encodeURIComponent(token)}/deliverables/${encodeURIComponent(id)}/elevation?x=${x}&y=${y}`);
              const b = await r.json().catch(() => ({}));
              if (!r.ok) throw new Error(typeof b.detail === 'string' ? b.detail : "Couldn't read the elevation.");
              return b;
            }} loadMapPreview={loadMapPreview} />
          </main>
        )}
      </div>
      <footer style={{ marginTop: 80, borderTop: `1px solid ${t.hair}` }}>
        <div className="ss-wrap" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, paddingTop: 24, paddingBottom: 24, minHeight: 72 }}>
          <span style={{ fontSize: 13, color: t.mute }}>{creditLine(data && data.timeline && data.timeline.visits)}</span>
          <a href="https://www.brxs.ca/builders" style={{ fontSize: 13, color: t.ink2, textDecoration: 'none', minHeight: 44, padding: '0 8px', margin: '0 -8px', display: 'inline-flex', alignItems: 'center' }}>brxs.ca</a>
        </div>
      </footer>
    </div>
  );
}
